/**
 * ContributingModules — the "modules contributing to this package" checklist
 * for the Compliance Hub (CLARID-UX-2026-06, PR 2).
 *
 * The Compliance Hub was the audit's worst page (1.5/5) precisely because a
 * first-time user couldn't tell it aggregates every module. This makes the moat
 * explicit: it lists the module groups that feed the exam/board package and
 * shows which the current plan includes — which doubles as the Core→Enterprise
 * upsell narrative. The contributor list mirrors `helpContent['compliance-hub']
 * .needs`; the plan check uses the existing feature gates.
 */

import { Link } from 'react-router-dom';
import { CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { moduleRoutes } from '@/lib/help/moduleRoutes';
import type { ModuleKey } from '@/lib/help';

interface Contributor {
  key: ModuleKey;
  /** One line on what this module contributes to the package. */
  contributes: string;
  /** Feature gate; omit for modules that are part of every plan. */
  feature?: string;
}

const CONTRIBUTORS: Contributor[] = [
  { key: 'marketing-upload', contributes: 'Advertising & UDAAP review' },
  { key: 'hmda-lar-validation', contributes: 'Mortgage data, edits & quality' },
  { key: 'fair-lending', contributes: 'Denial, pricing & redlining findings', feature: 'fair_lending' },
  { key: 'cra', contributes: 'CRA performance & gaps', feature: 'module_cra' },
  { key: 'enforcement-intelligence', contributes: 'Industry enforcement exposure', feature: 'enforcement_intelligence' },
];

interface ContributingModulesProps {
  className?: string;
}

export default function ContributingModules({ className }: ContributingModulesProps) {
  const { hasFeature } = useFeatureAccess();

  return (
    <div className={clsx('rounded-lg border border-gray-200 bg-white p-4', className)}>
      <h3 className="text-sm font-semibold text-gray-900">Modules this package draws from</h3>
      <p className="mt-1 text-xs text-gray-500">
        Clarid aggregates findings across every module into one exam- and board-ready package — the
        cross-module view no single-purpose tool offers.
      </p>
      <ul className="mt-3 divide-y divide-gray-100">
        {CONTRIBUTORS.map(({ key, contributes, feature }) => {
          const included = !feature || hasFeature(feature);
          const route = moduleRoutes[key];
          return (
            <li key={key} className="flex items-center justify-between gap-3 py-2.5">
              <div className="flex items-center gap-2.5">
                {included ? (
                  <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
                ) : (
                  <LockClosedIcon className="h-5 w-5 flex-shrink-0 text-gray-300" />
                )}
                <div className="min-w-0">
                  <Link
                    to={route.path}
                    className="text-sm font-medium text-gray-900 hover:text-teal-700 hover:underline"
                  >
                    {route.label}
                  </Link>
                  <p className="truncate text-xs text-gray-500">{contributes}</p>
                </div>
              </div>
              <span
                className={clsx(
                  'inline-flex flex-shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                  included ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                )}
              >
                {included ? 'Included' : 'Add-on'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
